import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Chip,
  MenuItem,
  Select,
  FormControl,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Grid,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Alert
} from '@mui/material';
import OrderLocationMap from '../components/OrderLocationMap';
import { Phone } from '@mui/icons-material';

import {
  ShoppingBag as ShoppingBagIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Home as AddressIcon,
  CreditCard as PaymentIcon,
  CalendarToday as DateIcon,
  Update as UpdateIcon,
  LocalOffer as ProductIcon,
  AttachMoney as PriceIcon,
  FormatListNumbered as QuantityIcon,
  Discount as DiscountIcon,
  Savings as SavingsIcon
} from '@mui/icons-material';
import api from '../services/api'

const OrderDetails = () => {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchOrderDetails = async () => {
      try {
        const response = await api.get(`/orders/${orderId}`);
        setOrder(response.data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
  };

  useEffect(() => {
    fetchOrderDetails();
  }, [orderId]);

  const updateStatus = async (orderId, newStatus) => {
    try {
      await api.put(`/admin/orders/${orderId}/status`, { status: newStatus });
      fetchOrderDetails(); // Refresh data
    } catch (err) {
      console.error('Status update error:', err);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'completed': return 'success';
      case 'canceled': return 'error';
      default: return 'primary';
    }
  };
  
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth' // Adds smooth scrolling animation
    });
  });

  const statusTxt = {
  canceled: 'ملغي',
  pending: 'معلق',
  processing: 'قيد التحضير',
  shipped: 'قيد التوصيل',
  delivered: 'مكتمل'
};

  const formatPaymentMethod = (method) => {
    return method.replace(/_/g, ' ');
  };

  // Calculate total savings from order items
  const totalSavings = order?.items?.reduce((total, item) => {
    const originalPrice = item.original_price || item.price;
    return total + (originalPrice - item.price) * item.quantity;
  }, 0) || 0;

  // Calculate original total before discounts
  const originalTotal = order?.items?.reduce((total, item) => {
    return total + (item.original_price || item.price) * item.quantity;
  }, 0) || 0;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!order) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4">لم يتم العثور على الطلب</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, mb: 10 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
          <ShoppingBagIcon />
        </Avatar>&nbsp;
        <Typography variant="h4"> طلب رقم #{order.id}</Typography>
        <Chip
          label={statusTxt[order.status]}
          color={getStatusColor(order.status)}
          sx={{ ml: 2, textTransform: 'capitalize' }}
        />
      </Box>

      {/* Savings Banner */}
      {totalSavings > 0 && (
        <Paper elevation={2} sx={{ 
          p: 3, 
          mb: 3, 
          backgroundColor: 'success.light',
          background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
          color: 'white'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <SavingsIcon sx={{ fontSize: 40 }} />
            <Box>
              <Typography variant="h6" fontWeight="bold">
                🎉 العميل قام بتوفير E.G.P {totalSavings.toFixed(2)}!
              </Typography>
              <Typography variant="body2">
                تبلغ نسبة الخصم {((totalSavings / originalTotal) * 100).toFixed(1)}% من هاذا الطلب
              </Typography>
            </Box>
          </Box>
        </Paper>
      )}

      <Grid container spacing={3}>
                {/* Shipping & Payment */}
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <AddressIcon sx={{ mr: 1 }} /> معلومات التوصيل / الدفع
            </Typography>
            <List>
              <ListItem>
                {/* Google Map */}
                <OrderLocationMap
                   latitude={order.latitude}
                   longitude={order.longitude}
                   height="300px"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <AddressIcon />
                </ListItemIcon>
                <ListItemText primary={order.shipping_address} secondary="عنوان التوصيل" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <PaymentIcon />
                </ListItemIcon>
                <ListItemText 
                  primary={formatPaymentMethod(order.payment_method)} 
                  secondary="طريقة الدفع" 
                />
              </ListItem>
              <ListItem>
                <FormControl size="small" fullWidth>
                    <Select
                        disabled = {order.status === 'delivered'}
                        value={order.status}
                        onChange={(e) => updateStatus(order.id, e.target.value)}
                    >
                        <MenuItem value="pending">معلقة</MenuItem>
                        <MenuItem value="processing">قيد التحضير</MenuItem>
                        <MenuItem value="shipped">قيد التوصيل</MenuItem>
                        <MenuItem value="delivered">مكتمل</MenuItem>
                        <MenuItem value="canceled">ملغي</MenuItem>
                    </Select>
                </FormControl>
              </ListItem>
            </List>
          </Paper>
        </Grid>

        {/* Customer Information */}
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <PersonIcon sx={{ mr: 1 }} /> معلومات العميل
            </Typography>
            <List>
              <ListItem>
                <ListItemIcon>
                  <PersonIcon />
                </ListItemIcon>
                <ListItemText primary={order.user?.username} secondary="إسم العميل" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <EmailIcon />
                </ListItemIcon>
                <ListItemText primary={order.user?.email} secondary="الإيميل" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <Phone />
                </ListItemIcon>
                <ListItemText primary={order.user?.user_phone} secondary="رقم الهاتف" />
              </ListItem>
            </List>
          </Paper>
        </Grid>

        {/* Order Timeline */}
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <DateIcon sx={{ mr: 1 }} /> الجدول الزمني للطلب
            </Typography>
            <List>
              <ListItem>
                <ListItemIcon>
                  <DateIcon />
                </ListItemIcon>
                <ListItemText 
                  primary={new Date(order.created_at).toLocaleString()} 
                  secondary="تم إنشاءه" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <UpdateIcon />
                </ListItemIcon>
                <ListItemText 
                  primary={new Date(order.updated_at).toLocaleString()} 
                  secondary="أخر تحديث" 
                />
              </ListItem>
            </List>
          </Paper>
        </Grid>

        {/* Order Items */}
        <Grid item xs={12}>
          <Paper elevation={3} sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <ProductIcon sx={{ mr: 1 }} /> محتوى السلة
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>إسم المنتج</TableCell>
                    <TableCell align="right">الكمية</TableCell>
                    <TableCell align="right">سعر الحبة</TableCell>
                    <TableCell align="right">خصم</TableCell>
                    <TableCell align="right">الإجمالي</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {order.items.map(item => {
                    const originalPrice = item.original_price || item.price;
                    const isDiscounted = originalPrice > item.price;
                    const discountAmount = originalPrice - item.price;
                    const discountPercent = isDiscounted 
                      ? Math.round((discountAmount / originalPrice) * 100)
                      : 0;

                    return (
                      <TableRow key={item.id}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <ProductIcon color="action" sx={{ mr: 1 }} />
                            {item.product.name}
                            {isDiscounted && (
                              <Chip
                                label="خصم"
                                size="small"
                                color="success"
                                sx={{ ml: 1, height: 20 }}
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                            <QuantityIcon color="action" sx={{ mr: 1 }} />
                            {item.quantity}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                            <PriceIcon color="action" sx={{ mr: 1 }} />
                            {isDiscounted ? (
                              <Box sx={{ textAlign: 'right' }}>
                                <Typography variant="body2" color="error" fontWeight="bold">
                                  E.G.P {item.price}
                                </Typography>
                                <Typography variant="caption" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                                  E.G.P {originalPrice.toFixed(2)}
                                </Typography>
                              </Box>
                            ) : (
                              `E.G.P ${item.price}`
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          {isDiscounted ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                              <DiscountIcon color="success" sx={{ mr: 0.5 }} />
                              <Typography variant="body2" color="success.main">
                                -E.G.P {discountAmount.toFixed(2)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                                ({discountPercent}%)
                              </Typography>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              -
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold">
                            E.G.P {(item.quantity * parseFloat(item.price)).toFixed(2)}
                          </Typography>
                          {isDiscounted && (
                            <Typography variant="caption" color="success.main" display="block">
                              Saved E.G.P {(discountAmount * item.quantity).toFixed(2)}
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  
                  {/* Order Summary */}
                  {totalSavings > 0 && (
                    <>
                      <TableRow>
                        <TableCell colSpan={4} align="right">
                          <Typography variant="body2">المجموع الفرعي (الأصلي):</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ textDecoration: 'line-through' }}>
                            E.G.P {originalTotal.toFixed(2)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={4} align="right">
                          <Typography variant="body2" color="success.main">
                            <DiscountIcon sx={{ fontSize: 16, verticalAlign: 'text-bottom', mr: 0.5 }} />
                            إجمالي الخصم :
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" color="success.main">
                            -E.G.P {totalSavings.toFixed(2)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </>
                  )}
                  
                  <TableRow sx={{ '& td': { borderBottom: 'none' } }}>
                    <TableCell colSpan={4} align="right">
                      <Typography variant="subtitle1" fontWeight="bold">
                        إجمالي الطلب :
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography 
                        variant="h6" 
                        color={totalSavings > 0 ? 'success.main' : 'primary.main'}
                        fontWeight="bold"
                      >
                        E.G.P {order.total_amount}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default OrderDetails;